import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  usePerformAdminActionMutation,
  useReopenCaseMutation,
  useSetDueDateMutation,
} from 'store/services/case/caseApi';
import { CaseStatus, AdminActionType, isTerminalCaseStatus } from 'types/interfaces/case';
import './AdminActionsPanel.css';

interface Props {
  caseId: string;
  caseStatus: CaseStatus;
  /** The trade's own status, for the note on a settling action the backend no longer allows. */
  tradeStatus?: string;
  /**
   * Server-computed set of actions this case accepts (Asana 1219265535165249).
   * undefined = older backend: fall back to status-only gating.
   */
  allowedActions?: string[];
  /** The backend exposes reopen (the reopenedAt key is present on the case). */
  canReopen?: boolean;
}

/**
 * The backend names the two settling resolutions COMPLETE_NORMAL / CANCEL_NORMAL
 * in allowedActions; the panel's buttons and the actions POST keep the FE enum.
 * Both spellings count as allowed.
 */
const ACTION_ALIASES: Record<string, string[]> = {
  [AdminActionType.COMPLETE_TRADE]: [AdminActionType.COMPLETE_TRADE, 'COMPLETE_NORMAL'],
  [AdminActionType.CANCEL_TRADE]: [AdminActionType.CANCEL_TRADE, 'CANCEL_NORMAL'],
};

const isAllowed = (allowedActions: string[] | undefined, action: AdminActionType): boolean => {
  if (!allowedActions) return true;
  const names = ACTION_ALIASES[action] ?? [action];
  return names.some((n) => allowedActions.includes(n));
};

const REOPEN_REASON_MAX = 500;

const AdminActionsPanel: React.FC<Props> = ({ caseId, caseStatus, tradeStatus, allowedActions, canReopen = false }) => {
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [performAction, { isLoading: isPerformingAction }] = usePerformAdminActionMutation();
  const [setDueDate, { isLoading: isSettingDueDate }] = useSetDueDateMutation();
  const [reopenCase, { isLoading: isReopening }] = useReopenCaseMutation();

  const isResolved = isTerminalCaseStatus(caseStatus);
  const tradeSettled = tradeStatus === 'COMPLETED' || tradeStatus === 'CANCELLED';
  const settledNote = tradeSettled
    ? `The trade is already ${tradeStatus === 'COMPLETED' ? 'completed' : 'cancelled'}; this action is no longer available.`
    : 'This action is not available for this case.';

  const handleReopen = async () => {
    if (!window.confirm('Reopen this case? It returns to In Progress and any scheduled auto-reopen is cancelled.')) return;
    try {
      await reopenCase({ caseId, reason: reopenReason.trim() || undefined }).unwrap();
      toast.success('Case reopened');
      setReopenReason('');
    } catch (error: any) {
      console.error('Failed to reopen case:', error);
      // 409: the case is already open (another admin got there first); the
      // detail refetches through tag invalidation on the next action anyway.
      toast.error(error?.data?.message || 'Failed to reopen the case. Please try again.');
    }
  };

  const handleAction = async (
    actionType:
      | AdminActionType.COMPLETE_TRADE
      | AdminActionType.CANCEL_TRADE
      | AdminActionType.REFUND_BOTH
      | AdminActionType.NO_ACTION
  ) => {
    const actionLabels = {
      [AdminActionType.COMPLETE_TRADE]: 'complete this trade',
      [AdminActionType.CANCEL_TRADE]: 'cancel this trade',
      [AdminActionType.REFUND_BOTH]: 'refund both parties',
      [AdminActionType.NO_ACTION]: 'resolve this case with no action'
    };

    const confirmMessage =
      actionType === AdminActionType.NO_ACTION
        ? `Are you sure you want to ${actionLabels[actionType]}? Resolves silently. No message or email is sent to participants. This action cannot be undone.`
        : `Are you sure you want to ${actionLabels[actionType]}? This action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await performAction({
        caseId,
        actionType
      }).unwrap();

      toast.success(`Action completed: ${actionType.replace(/_/g, ' ').toLowerCase()}`);
    } catch (error: any) {
      console.error('Failed to perform action:', error);
      // 409 = the trade is already completed or cancelled (backend guard on a
      // reopened case); the message says which, and the detail refetches.
      toast.error(error.data?.message || 'Failed to perform action. Please try again.');
    }
  };

  const handleSetDueDate = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    const dueDate = new Date(selectedDate);
    const now = new Date();

    // Validate future date
    if (dueDate <= now) {
      toast.error('Due date must be in the future');
      return;
    }

    try {
      await setDueDate({
        caseId,
        dueDate: dueDate.toISOString()
      }).unwrap();

      toast.success(`Due date set to ${dueDate.toLocaleDateString()}`);
      setShowDueDatePicker(false);
      setSelectedDate('');
    } catch (error: any) {
      console.error('Failed to set due date:', error);
      toast.error(error.data?.message || 'Failed to set due date. Please try again.');
    }
  };

  const busy = isPerformingAction || isSettingDueDate || isReopening;
  const isActionDisabled = isResolved || busy;
  /** Per-action gate: the shared disabled rule plus the server's allowed set. */
  const disabledFor = (action: AdminActionType) => isActionDisabled || !isAllowed(allowedActions, action);
  const titleFor = (action: AdminActionType, base: string) =>
    !isResolved && !isAllowed(allowedActions, action) ? settledNote : base;

  return (
    <div className="admin-actions-panel">
      <h2 className="panel-title">Admin Actions</h2>
      <p className="admin-actions-helper">
        These apply to the whole trade. Per-card Edit / Complete / Cancel are on each row below.
      </p>

      {isResolved && (
        <div className="resolved-notice" data-testid="resolved-notice">
          <p className="resolved-notice__text">
            This case has been {caseStatus === CaseStatus.CLOSED ? 'closed' : 'resolved'}. No further actions can be
            taken unless it is reopened.
          </p>
          {canReopen && (
            <div className="reopen-section" data-testid="reopen-section">
              <label className="reopen-label" htmlFor="reopen-reason">
                Reason (optional)
              </label>
              <textarea
                id="reopen-reason"
                className="reopen-reason"
                value={reopenReason}
                maxLength={REOPEN_REASON_MAX}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Why is this case being reopened?"
                rows={2}
              />
              <button
                type="button"
                className="case-action-button case-action-reopen"
                onClick={handleReopen}
                disabled={busy}
                data-testid="reopen-button"
              >
                <span className="case-action-icon">↺</span>
                {isReopening ? 'Reopening…' : 'Reopen case'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="case-action-buttons">
        <button
          className="case-action-button case-action-complete"
          onClick={() => handleAction(AdminActionType.COMPLETE_TRADE)}
          disabled={disabledFor(AdminActionType.COMPLETE_TRADE)}
          title={titleFor(AdminActionType.COMPLETE_TRADE, 'Mark the trade as completed and credit the sender')}
        >
          <span className="case-action-icon">✓</span>
          Complete Trade
        </button>

        <button
          className="case-action-button case-action-cancel"
          onClick={() => handleAction(AdminActionType.CANCEL_TRADE)}
          disabled={disabledFor(AdminActionType.CANCEL_TRADE)}
          title={titleFor(AdminActionType.CANCEL_TRADE, 'Cancel the trade (no points transferred)')}
        >
          <span className="case-action-icon">✕</span>
          Cancel Trade
        </button>

        <button
          className="case-action-button case-action-refund"
          onClick={() => handleAction(AdminActionType.REFUND_BOTH)}
          disabled={disabledFor(AdminActionType.REFUND_BOTH)}
          title={titleFor(AdminActionType.REFUND_BOTH, 'Cancel trade and refund both parties')}
        >
          <span className="case-action-icon">↻</span>
          Refund Both Parties
        </button>

        <div className="case-action-no-action-group">
          <button
            className="case-action-button case-action-no-action"
            onClick={() => handleAction(AdminActionType.NO_ACTION)}
            disabled={disabledFor(AdminActionType.NO_ACTION)}
            title={titleFor(AdminActionType.NO_ACTION, 'Resolve the case without taking any action on the trade')}
          >
            <span className="case-action-icon">○</span>
            No Action
          </button>
          <p className="case-action-helper">
            Resolves silently. No message or email is sent to participants.
          </p>
        </div>

        <button
          className="case-action-button case-action-due-date"
          onClick={() => setShowDueDatePicker(!showDueDatePicker)}
          disabled={disabledFor(AdminActionType.SET_DUE_DATE)}
          title={titleFor(AdminActionType.SET_DUE_DATE, 'Set a due date for case follow-up')}
        >
          <span className="case-action-icon">📅</span>
          Set Due Date
        </button>
      </div>

      {showDueDatePicker && !isResolved && (
        <div className="due-date-picker-section">
          <h3 className="picker-title">Set Due Date</h3>
          <p className="picker-description">
            The case will be marked as resolved and reopened automatically on the selected date.
          </p>
          <div className="picker-inputs">
            <input
              type="date"
              className="date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
            <div className="picker-actions">
              <button
                className="picker-button picker-confirm"
                onClick={handleSetDueDate}
                disabled={!selectedDate || isSettingDueDate}
              >
                {isSettingDueDate ? 'Setting...' : 'Confirm'}
              </button>
              <button
                className="picker-button picker-cancel"
                onClick={() => {
                  setShowDueDatePicker(false);
                  setSelectedDate('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="action-info">
        <h3 className="info-title">Action Details:</h3>
        <ul className="info-list">
          <li>
            <strong>Complete Trade:</strong> Marks trade as completed, credits sender (minus fees), and resolves case
          </li>
          <li>
            <strong>Cancel Trade:</strong> Cancels the trade, no points transferred, resolves case
          </li>
          <li>
            <strong>Refund Both:</strong> Cancels trade, refunds receiver, credits sender (minus fees), resolves case
          </li>
          <li>
            <strong>No Action:</strong> Resolves the case without affecting the trade status. Resolves silently — no message or email is sent to participants
          </li>
          <li>
            <strong>Set Due Date:</strong> Resolves case and automatically reopens it on the selected date
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdminActionsPanel;
